<template>
  <div>
    <div class="d-flex align-center justify-space-between">
      <span class="font-size-14 font-weight-medium">
        {{ label }}: <strong>{{ localFiles.length }}</strong>
      </span>

      <v-btn
        v-if="!disabled"
        :loading="$loading.isLoading(uploadLoadingKey)"
        color="light-blue-darken-4"
        @click="selectAndUpload">
        <v-icon class="mr-1" icon="mdi-plus"></v-icon>
        <span>Додати файл</span>
      </v-btn>
    </div>

    <div v-if="localFiles.length" class="mt-3">
      <v-card
        v-for="file in localFiles"
        :key="file.id || file.path"
        border
        class="mb-2">
        <v-card-text class="d-flex align-center justify-space-between ga-3">
          <div class="d-flex align-center ga-3">
            <v-sheet
              :color="getExtensionColor(file)"
              class="d-flex align-center justify-center"
              height="48"
              rounded="lg"
              width="48">
              <span class="text-uppercase font-weight-bold font-size-11">
                {{ getExtension(file) }}
              </span>
            </v-sheet>

            <div>
              <div class="font-weight-bold font-size-14">{{ file.name }}</div>
              <div class="text-disabled font-size-12 d-flex align-center ga-3">
                <div v-if="file.uploader" class="d-flex align-center ga-2">
                  <v-sheet
                    class="d-flex align-center justify-center"
                    color="blue-lighten-4"
                    height="22"
                    rounded="circle"
                    width="22">
                    <v-icon
                      color="blue-darken-3"
                      icon="mdi-account-outline"
                      size="x-small"></v-icon>
                  </v-sheet>
                  <span>
                    {{ getPersonName(file.uploader, { initials: true }) }}
                  </span>
                </div>

                <div v-if="file.created_at" class="d-flex align-center ga-1">
                  <v-icon icon="mdi-timer-outline" size="x-small"></v-icon>
                  <span>
                    {{
                      new CustomDate(file.created_at).toString({
                        time: true,
                      })
                    }}
                  </span>
                </div>

                <div class="d-flex align-center ga-1">
                  <v-icon icon="mdi-file-outline" size="x-small"></v-icon>
                  <span>{{ formatFileSize(file.size) }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="d-flex align-center ga-1">
            <v-btn
              v-tooltip="'Переглянути'"
              border
              color="white"
              density="comfortable"
              icon
              size="small"
              @click="openDocument(file.path)">
              <v-icon size="small">mdi-light:eye</v-icon>
            </v-btn>
            <v-btn
              v-tooltip="'Завантажити'"
              border
              color="white"
              density="comfortable"
              icon
              size="small"
              @click="download(file.path, file.name)">
              <v-icon size="small">mdi-light:download</v-icon>
            </v-btn>
            <v-btn
              v-if="!disabled"
              v-tooltip="'Видалити'"
              border
              icon
              color="white"
              density="comfortable"
              size="small"
              @click="remove(file)">
              <v-icon color="error" size="small">mdi-light:delete</v-icon>
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <empty-section
      v-else
      class="mt-3"
      :description="emptyDescription"
      :title="emptyTitle"></empty-section>
  </div>
</template>

<script setup lang="ts">
import EmptySection from '@/components/EmptySection.vue'
import { selectFile } from '@/components/filesUploader/selectFile'
import { uploadFiles } from '@/components/filesUploader/uploadFiles'
import type { MainFolder } from '@/components/filesUploader/uploadFiles'
import { formatFileSize, openDocument } from '@/utils/helpers.js'
import type { File as StoredFile } from '@/utils/types/helpers'
import { setSnackbar } from 'best-modules/plugins'
import { CustomDate } from 'best-modules/utils'
import { getPersonName } from 'best-modules/utils/helpers'
import { download } from 'best-modules/vue-utils'
import moment from 'moment'
import { computed } from 'vue'
import { useStore } from 'vuex'

defineOptions({
  name: 'FileList',
})

type FileItem = Omit<StoredFile, 'id'> & {
  id?: string | number
  ext?: string
}

const props = withDefaults(
  defineProps<{
    accept?: string[]
    disabled?: boolean
    emptyDescription?: string
    emptyTitle?: string
    label?: string
    mainFolder?: MainFolder
    maxFileSizeMb?: number
    modelValue?: FileItem[]
    multiple?: boolean
    nameFolder: string
    prefixFolder?: string
    prepend?: boolean
    createFile?: (_file: FileItem) => Promise<FileItem>
    removeFile?: (_file: FileItem) => Promise<unknown>
  }>(),
  {
    accept: () => [
      '.png',
      '.jpg',
      '.jpeg',
      '.svg',
      '.doc',
      '.docx',
      '.pdf',
      '.xls',
      '.xlsx',
      '.odt',
      '.rar',
      '.zip',
    ],
    emptyDescription: "Додані файли з'являться тут",
    emptyTitle: 'Файлів ще немає',
    label: 'Прикріплено документів',
    mainFolder: 'Документы',
    maxFileSizeMb: 15,
    modelValue: () => [],
    multiple: true,
  }
)

const emit = defineEmits<{
  'update:modelValue': [files: FileItem[]]
}>()

const store = useStore()
const user = computed(() => store.state.user.userData)
const localFiles = computed(() => props.modelValue)
const uploadLoadingKey = computed(
  () => `uploadFile_${props.mainFolder}_${props.nameFolder}`
)

const getExtension = (file: FileItem) => {
  return (file.ext || file.path?.split('.').at(-1) || 'file').toLowerCase()
}

const getExtensionColor = (file: FileItem) => {
  switch (getExtension(file)) {
    case 'pdf':
      return 'red-lighten-5'
    case 'png':
    case 'jpg':
    case 'jpeg':
      return 'purple-lighten-5'
    default:
      return 'grey-lighten-4'
  }
}

const addMetadata = (files: FileItem[]) => {
  const created_at = moment().format('YYYY-MM-DD HH:mm:ss')

  return files.map(file => ({
    created_at,
    uploader: user.value,
    ...file,
  }))
}

const remove = async (file: FileItem) => {
  await props.removeFile?.(file)
  emit(
    'update:modelValue',
    props.modelValue.filter(item => item.path !== file.path)
  )
}

const createUploadedFile = (file: FileItem) => {
  if (!props.createFile) return Promise.resolve(file)

  return props.createFile({
    ...file,
    uploadedBy: user.value.id,
  })
}

const selectAndUpload = () => {
  return selectFile({
    accept: props.accept,
    multiple: props.multiple,
  }).then(event => {
    const files = event.blob.filter(file => {
      return file.size <= props.maxFileSizeMb * 1024 * 1024
    })

    if (files.length !== event.blob.length) {
      setSnackbar({
        color: 'error',
        text: `Максимальний розмір файлу: ${props.maxFileSizeMb} МБ`,
      })
    }

    if (!files.length) return

    return uploadFiles({
      files,
      mainFolder: props.mainFolder,
      nameFolder: props.nameFolder,
      prefixFolder: props.prefixFolder,
    }).then(async ({ files }) => {
      const uploadedFiles = files as FileItem[]
      const createdFiles = props.createFile
        ? await Promise.all(uploadedFiles.map(createUploadedFile))
        : addMetadata(uploadedFiles)

      emit(
        'update:modelValue',
        props.prepend
          ? [...createdFiles, ...props.modelValue]
          : [...props.modelValue, ...createdFiles]
      )
    })
  })
}
</script>
